import { useMemo } from "react";
import { cmsUrl } from "@kozien/shared";
import { resolveDecorationImageUrl } from "@/models/color/resolveDecorationImageUrl";
import { resolveSelectionColors } from "@/models/color/resolveSelectionColors";
import { type ProductType, useProducts } from "@/models/product";
import {
    type ConfiguredCombo,
    getAllConfiguredCombos,
    useSelectionStore,
} from "@/store/selection";
import { useTotalPrice } from "@/models/price";
import type { ProductPrice, ProductSurface } from "@/models/price/price.types";
import { getDisplayAmount } from "@/lib/getDisplayAmount";
import { useSunProtectionPageContent } from "@/models/sun-protection-settings";
import type { WindowEntry, WindowEntryChip } from "./types";

const CATEGORY_ORDER: Record<ProductType, number> = {
    SUN_PROTECTION: 0,
    WINDOW_DECORATION: 1,
};

type DealProduct = ReturnType<
    typeof useSelectionStore.getState
>["dealProducts"][number];

type CmsProduct = ReturnType<typeof useProducts>["products"][number];

function buildChip(
    combo: ConfiguredCombo,
    windowId: string,
    cmsProduct: CmsProduct | undefined,
    productPrices: ProductPrice[],
    productSurfaces: ProductSurface[],
    pricesIncludeVat: boolean | null | undefined,
): WindowEntryChip | null {
    const sel = combo.windowSelections[windowId];
    if (!sel) return null;

    const colors = resolveSelectionColors(cmsProduct, sel, combo.typeId);

    const productImageUrl =
        cmsUrl(
            resolveDecorationImageUrl(
                cmsProduct ?? null,
                sel.colorId,
                sel.frameColorId,
            ),
            import.meta.env.VITE_CMS_BASE_ENDPOINT,
        ) ?? null;

    const price =
        getDisplayAmount(
            pricesIncludeVat,
            productPrices.find(
                (p) =>
                    p.productId === windowId &&
                    p.sunProtectionProductId === combo.typeId,
            )?.total,
        ) ?? null;

    // Sun protection covers the whole window; window decoration is sold per
    // surface unit, hence productSurfaces.
    const quantity =
        combo.category === "SUN_PROTECTION"
            ? 1
            : (productSurfaces.find((s) => s.productId === windowId)?.count ??
              1);

    return {
        category: combo.category,
        typeId: combo.typeId,
        productImageUrl,
        ...colors,
        controlType: sel.controlType,
        price,
        quantity,
    };
}

function buildEntry(windowId: string, dealProduct: DealProduct): WindowEntry {
    return {
        windowId,
        locationName: dealProduct.locationName,
        previewImageUrl:
            cmsUrl(
                dealProduct.previewImage?.url,
                import.meta.env.VITE_CMS_BASE_ENDPOINT,
            ) ?? null,
        chips: [],
    };
}

export type ConfirmRows = {
    entries: WindowEntry[];
    groupedEntries: Array<[string, WindowEntry[]]>;
    totalProductCount: number;
};

/**
 * Joins configured combos with deal products, CMS products, prices and
 * surfaces into the confirm step's view-model. Entries are sorted in deal
 * order and partitioned into consecutive location groups; chips within an
 * entry are sun-protection-first. Memoised so identities stay stable.
 */
export function useConfirmRows(): ConfirmRows {
    const state = useSelectionStore();
    const { dealProducts } = state;
    const { productPrices, productSurfaces } = useTotalPrice();
    const sunProducts = useProducts("SUN_PROTECTION").products;
    const windowProducts = useProducts("WINDOW_DECORATION").products;
    const combos = getAllConfiguredCombos(state);
    const { data: pageContent } = useSunProtectionPageContent();
    const pricesIncludeVat = pageContent?.pricesIncludeVat;

    return useMemo(() => {
        const allProducts = [...sunProducts, ...windowProducts];
        const entries = new Map<string, WindowEntry>();
        for (const combo of combos) {
            const cmsProduct = allProducts.find((p) => p.id === combo.typeId);
            for (const windowId of combo.selectedProductIds) {
                const dealProduct = dealProducts.find((p) => p.id === windowId);
                if (!dealProduct) continue;
                const chip = buildChip(
                    combo,
                    windowId,
                    cmsProduct,
                    productPrices,
                    productSurfaces,
                    pricesIncludeVat,
                );
                if (!chip) continue;
                if (!entries.has(windowId)) {
                    entries.set(windowId, buildEntry(windowId, dealProduct));
                }
                entries.get(windowId)!.chips.push(chip);
            }
        }

        for (const entry of entries.values()) {
            entry.chips.sort(
                (a, b) =>
                    CATEGORY_ORDER[a.category] - CATEGORY_ORDER[b.category],
            );
        }

        const dealOrder = new Map(dealProducts.map((p, idx) => [p.id, idx]));
        const sortedEntries = [...entries.values()].sort(
            (a, b) =>
                (dealOrder.get(a.windowId) ?? 0) -
                (dealOrder.get(b.windowId) ?? 0),
        );

        const groupedEntries: Array<[string, WindowEntry[]]> = [];
        for (const entry of sortedEntries) {
            const last = groupedEntries[groupedEntries.length - 1];
            if (last && last[0] === entry.locationName) {
                last[1].push(entry);
            } else {
                groupedEntries.push([entry.locationName, [entry]]);
            }
        }

        const totalProductCount = sortedEntries.reduce(
            (sum, e) => sum + e.chips.reduce((cSum, c) => cSum + c.quantity, 0),
            0,
        );

        return {
            entries: sortedEntries,
            groupedEntries,
            totalProductCount,
        };
    }, [
        dealProducts,
        productPrices,
        productSurfaces,
        sunProducts,
        windowProducts,
        combos,
        pricesIncludeVat,
    ]);
}
